import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft,
  Clock,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Instagram,
  Facebook,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Users,
  Headphones,
  HelpCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string;
}

const Suporte: React.FC = () => {
  const navigate = useNavigate();
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const faqItems: FAQItem[] = [
    {
      question: "Preciso pagar para participar do programa de recompensas?",
      answer: "Não! O programa é totalmente gratuito e exclusivo para clientes e revendedoras X do Infinito. Você acumula diamantes automaticamente, que podem ser trocados por recompensas reais na Loja de Recompensas."
    },
    {
      question: "Como funcionam os diamantes?",
      answer: "Os diamantes são pontos acumulados a cada compra ou indicação feita através do aplicativo Xizinha. Eles ficam registrados no aplicativo e podem ser trocados por semijoias, eletrônicos, acessórios e outros produtos exclusivos na Loja de Recompensas."
    },    
    {
      question: "Como funciona o programa de indicações?",
      answer: "Ao indicar novas revendedoras ou clientes, você acumula diamantes em até 3 níveis da sua rede. Além disso, há bônus extras de ativação na primeira compra realizada pelas suas indicadas diretas."
    },   
    {
      question: "Como acompanho minhas compras e indicações?",
      answer: "Dentro do aplicativo, você pode acompanhar em tempo real suas compras, os diamantes acumulados, histórico de resgates e os detalhes da sua rede de indicações de forma simples e prática."
    },
    {
      question: "Como sei se os diamantes foram creditados?",
      answer: "Dentro do aplicativo, você acompanha em tempo real os diamantes acumulados a cada compra ou indicação."
    },
    {
      question: "E se eu não tiver uma revendedora?",
      answer: "Se você ainda não conhece nenhuma revendedora X do Infinito, basta acionar a opção \"Suporte\" no menu do aplicativo. Nossa equipe vai indicar a mais próxima para atender você."
    },
    {
      question: "Como faço para me tornar uma revendedora?",
      answer: "É simples! Basta entrar em contato conosco pelo WhatsApp e preencher o pré-cadastro. Nossa equipe irá orientar você em todas as etapas, explicando os requisitos e mostrando todos os benefícios de revender X do Infinito."
    }
  ];

  const openWhatsApp = () => {
    window.open('https://api.whatsapp.com/send?phone=554591559222&text=Ol%C3%A1,%20vim%20do%20app%20Xizinha%20e%20preciso%20de%20suporte', '_blank');
  };

  const openInstagram = () => {
    window.open('https://www.instagram.com/xdoinfinito/', '_blank');
  };

  const openFacebook = () => {
    window.open('https://www.facebook.com/xdoinfinito', '_blank');
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background pb-12">
      {/* Header */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-sm border-b border-gray-200 z-10">
        <div className="px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Suporte</h1>
            <p className="text-sm text-gray-500">Central de ajuda e atendimento</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-6 space-y-6">
        {/* Card de Boas-vindas */}
        <Card className="bg-gradient-to-r from-primary to-primary-hover border-primary/20 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-white/20 rounded-full">
                <Headphones className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Conte conosco</h2>
                <p className="text-white/90 text-sm">Nossa equipe está pronta para te ajudar através dos canais de atendimento a seguir.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Horário de Atendimento */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Horário de Atendimento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="font-medium">Segunda a Sexta-feira</span>
              {/* <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50">
                08:00 - 18:00
              </Badge> */}
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Manhã</span>
              <Badge variant="outline" className="text-blue-700 border-blue-300 bg-blue-50">
                08:00 - 12:00
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Tarde</span>
               <Badge variant="outline" className="text-blue-700 border-blue-300 bg-blue-50">
                13:30 - 18:00
              </Badge>
            </div>            
          </CardContent>
        </Card>

        {/* Contatos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              Contatos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* WhatsApp */}
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-full">
                  <MessageCircle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-green-800">WhatsApp</p>
                  <p className="text-sm text-green-700">(45) 99155-9222</p>
                </div>
              </div>
              <Button 
                size="sm" 
                onClick={openWhatsApp}
                className="bg-green-500 hover:bg-green-600"
              >
                <MessageCircle className="w-4 h-4 mr-1" />
                Conversar
              </Button>
            </div>

            {/* E-mail */}
            {/* <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-full">
                  <Mail className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-blue-800">E-mail</p>
                  <p className="text-sm text-blue-700">contato@xdoinfinito.com.br</p>
                </div>
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => window.location.href = 'mailto:contato@xdoinfinito.com.br'}
              >
                <Mail className="w-4 h-4 mr-1" />
                Enviar
              </Button>
            </div> */}
          </CardContent>
        </Card>

        {/* Localização */}
        {/* <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Visite-nos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500 rounded-full">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-orange-800">Cascavel - PR</p>
                  <p className="text-sm text-orange-700">Paraná, Brasil</p>
                </div>
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={openGoogleMaps}
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Ver no Mapa
              </Button>
            </div>
          </CardContent>
        </Card> */}

        {/* Redes Sociais */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Redes Sociais
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Instagram */}
            <div className="flex items-center justify-between p-3 bg-pink-50 rounded-lg border border-pink-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full">
                  <Instagram className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-pink-800">Instagram</p>
                  <p className="text-sm text-pink-700">@xdoinfinito</p>
                </div>
              </div>
              <Button 
                size="sm" 
                onClick={openInstagram}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                <Instagram className="w-4 h-4 mr-1" />
                Seguir
              </Button>
            </div>

            {/* Facebook */}
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600 rounded-full">
                  <Facebook className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-blue-800">Facebook</p>
                  <p className="text-sm text-blue-700">X do Infinito</p>
                </div>
              </div>
              <Button 
                size="sm" 
                onClick={openFacebook}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Facebook className="w-4 h-4 mr-1" />
                Curtir
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary" />
              Perguntas Frequentes (FAQ)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {faqItems.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900">{item.question}</span>
                  {expandedFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {expandedFAQ === index && (
                  <div className="px-4 pb-4">
                    <p className="text-gray-600 text-sm leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Call to Action */}
        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <CardContent className="p-6 text-center">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 text-white/80" />
            <h3 className="text-lg font-semibold mb-2">Não encontrou o que procurava?</h3>
            <p className="text-white/90 text-sm mb-4">
              Nossa equipe está pronta para ajudar! Entre em contato conosco pelo WhatsApp.
            </p>
            <Button 
              onClick={openWhatsApp}
              className="bg-white text-green-600 hover:bg-gray-100"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Falar com Suporte
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Espaçamento para navegação mobile */}
      <div className="h-20 lg:h-0"></div>
    </div>
  );
};

export default Suporte;
